import { Breadcrumbs } from '../components/Breadcrumbs';
import { ScopingForm } from '../components/contact/ScopingForm';
import { StudioMap } from '../components/contact/StudioMap';
import { Faq, faqLd } from '../components/Faq';
import { Icon } from '../components/Icon';
import { Picture } from '../components/Picture';
import { contactFaqs, contactPrincipal, materialStats, trustItems } from '../data/contact';
import { breadcrumbLd, Seo } from '../lib/seo';
import { absoluteUrl, fullAddress, SITE } from '../lib/site';
import { container, eyebrow, h2Section } from '../lib/ui';

const PATH = '/contact';
const TITLE = 'Contact Our Nagercoil Studio';
const DESCRIPTION =
  'Contact Green Valley Interiors in Nagercoil: call, WhatsApp or send your project brief for a free interior design consultation in Kanyakumari and Trivandrum.';
const TRAIL: Array<[string, string]> = [['Contact', PATH]];

const whatsappHello = `${SITE.whatsappUrl}?text=${encodeURIComponent(
  'Hello Green Valley Interiors, I would like to schedule a private design consultation.',
)}`;

export default function Contact() {
  return (
    <>
      <Seo
        title={TITLE}
        description={DESCRIPTION}
        keywords={[
          'book interior design consultation in Nagercoil',
          'contact interior designers in Nagercoil',
          'interior designer on Asaripallam Road Nagercoil',
          'free home interior consultation in Kanyakumari',
          'villa interior design quote in Trivandrum',
          'office interior design enquiry in Tamil Nadu',
        ]}
        path={PATH}
        jsonLd={[
          breadcrumbLd(TRAIL),
          {
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            '@id': `${absoluteUrl(PATH)}#webpage`,
            url: absoluteUrl(PATH),
            name: `${TITLE} | ${SITE.name}`,
            description: DESCRIPTION,
            inLanguage: 'en-IN',
            isPartOf: { '@id': `${SITE.origin}/#website` },
            about: { '@id': `${SITE.origin}/#business` },
          },
          faqLd(contactFaqs),
        ]}
      />

      {/* HERO */}
      <div className="relative w-full overflow-hidden bg-surface-ivory pt-space-lg md:pt-space-xl pb-space-lg">
        <div className="absolute -top-32 -left-20 w-96 h-96 rounded-full bg-emerald-vivid/10 blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 -right-32 w-[32rem] h-[32rem] rounded-full bg-secondary-fixed/20 blur-3xl pointer-events-none" />
        <div className={`${container} relative z-10`}>
          <Breadcrumbs trail={TRAIL} className="mb-space-md" />
          <div className="flex items-center gap-space-sm mb-space-sm">
            <span className="w-8 h-px bg-secondary shrink-0" />
            <span className="font-label-sm text-label-sm uppercase tracking-widest text-secondary font-bold">
              Atelier Private Scoping • Tamil Nadu &amp; Kerala
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter gap-y-space-md items-end">
            <div className="lg:col-span-8 flex flex-col gap-space-xs">
              <h1 className="flex flex-col gap-space-xs animate-rise">
                <span className="font-label-md text-label-md uppercase tracking-[0.2em] text-primary">Contact Green Valley Interiors</span>
                <span className="font-headline-xl text-headline-xl-mobile md:text-headline-xl text-primary font-medium tracking-tight">
                  Initiate Your Spatial <span className="italic font-normal text-secondary">Metamorphosis.</span>
                </span>
              </h1>
              <p className="font-body-xl text-body-lg md:text-body-xl text-on-surface-variant max-w-2xl font-light animate-rise [animation-delay:80ms]">
                An invitation for discerning homeowners, real estate visionaries, and hospitality proprietors. We calibrate
                bespoke craftsmanship with architectural exactitude.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-space-sm justify-end items-start lg:items-end">
              <div className="bg-surface-cream/80 backdrop-blur-md px-space-md py-space-sm rounded shadow-sm flex items-center gap-space-sm">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-vivid animate-pulse shrink-0" />
                <span className="font-label-md text-label-md uppercase tracking-wider text-primary">Now Accepting New Commissions</span>
              </div>
              <div className="font-body-sm text-body-sm text-on-surface-variant lg:text-right">
                Confidential Consultations • Nagercoil Studio &amp; On-Site
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TRUST RIBBON */}
      <section className="w-full bg-surface-container-low py-space-md" aria-label="Our commitments">
        <div className={container}>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-space-sm md:gap-gutter">
            {trustItems.map((t) => (
              <li
                key={t.title}
                className="flex items-center gap-space-md p-space-sm rounded bg-surface-ivory shadow-sm transition-all duration-300 hover:-translate-y-0.5"
              >
                <div className="w-12 h-12 rounded bg-primary-container text-gold-light flex items-center justify-center shrink-0">
                  <Icon name={t.icon} size={24} />
                </div>
                <div className="flex flex-col">
                  <span className="font-headline-sm text-[18px] leading-7 text-primary">{t.title}</span>
                  <span className="font-body-sm text-body-sm text-on-surface-variant">{t.text}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* STUDIO + FORM */}
      <section className="w-full py-space-xl bg-surface" id="consultation">
        <div className={container}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter gap-y-space-lg items-start">
            <div className="lg:col-span-5 flex flex-col gap-space-lg">
              {/* Studio card */}
              <div className="bg-surface-cream rounded p-space-md sm:p-space-lg shadow-sm relative overflow-hidden">
                <div className="absolute -right-8 -bottom-8 opacity-5 text-primary select-none pointer-events-none">
                  <Icon name="compass_calibration" size={180} />
                </div>
                <div className="flex flex-col gap-space-xs relative z-10">
                  <span className="font-label-sm text-label-sm uppercase tracking-widest text-secondary font-semibold">Central Atelier</span>
                  <h2 className="font-headline-md text-headline-md text-primary">Nagercoil Studio &amp; Material Library</h2>
                  <p className="font-body-md text-body-md text-on-surface-variant mt-space-xs">
                    Located along the arterial Asaripallam corridor, our sanctuary welcomes clients to touch curated marble
                    slabs, tactile oak finishes, Italian veneers, and architectural fabric samples.
                  </p>
                </div>
                <address className="not-italic mt-space-lg flex flex-col gap-space-md relative z-10">
                  <div className="flex items-start gap-space-md">
                    <div className="w-10 h-10 rounded bg-surface-ivory text-primary flex items-center justify-center shrink-0 shadow-sm">
                      <Icon name="location_on" size={20} />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="font-label-sm text-label-sm uppercase text-on-surface-variant font-bold">Physical Address</span>
                      <p className="font-body-md text-body-md text-primary font-medium">{fullAddress}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-space-md">
                    <div className="w-10 h-10 rounded bg-surface-ivory text-primary flex items-center justify-center shrink-0 shadow-sm">
                      <Icon name="call" size={20} />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="font-label-sm text-label-sm uppercase text-on-surface-variant font-bold">Direct Phone line</span>
                      <a
                        href={`tel:${SITE.phoneE164}`}
                        className="font-headline-sm text-[20px] leading-8 text-primary hover:text-emerald-vivid transition-colors"
                      >
                        {SITE.phoneDisplay}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-space-md">
                    <div className="w-10 h-10 rounded bg-surface-ivory text-primary flex items-center justify-center shrink-0 shadow-sm">
                      <Icon name="mail" size={20} />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="font-label-sm text-label-sm uppercase text-on-surface-variant font-bold">Studio Enquiries</span>
                      <a
                        href={`mailto:${SITE.email}`}
                        className="font-body-md text-body-md text-primary hover:text-secondary transition-colors underline underline-offset-4 font-medium break-all"
                      >
                        {SITE.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-space-md">
                    <div className="w-10 h-10 rounded bg-surface-ivory text-primary flex items-center justify-center shrink-0 shadow-sm">
                      <Icon name="schedule" size={20} />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="font-label-sm text-label-sm uppercase text-on-surface-variant font-bold">Private Scoping Sessions</span>
                      <span className="font-body-md text-body-md text-primary">{SITE.hours.display}</span>
                      <span className="font-body-sm text-body-sm text-on-surface-variant">
                        Sunday: Private appointments by prior arrangement only
                      </span>
                    </div>
                  </div>
                </address>
                <div className="mt-space-lg pt-space-md relative z-10">
                  <a
                    href={whatsappHello}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-space-sm bg-[#0e7366] text-on-primary py-space-sm px-space-md rounded font-label-lg text-label-lg uppercase tracking-wider hover:brightness-110 shadow-md transition-all text-center"
                  >
                    <Icon name="chat" size={20} />
                    <span>Initiate Concierge WhatsApp Chat</span>
                  </a>
                </div>
              </div>

              <StudioMap />

              {/* Principal note */}
              <figure className="bg-primary text-on-primary rounded p-space-md sm:p-space-lg shadow-xl relative overflow-hidden">
                <figcaption className="flex items-center gap-space-md mb-space-sm">
                  <div className="w-14 h-14 rounded-full overflow-hidden shrink-0 bg-surface-dim">
                    <Picture
                      name="contact-principal-portrait"
                      alt={`Portrait of ${contactPrincipal.name}, ${contactPrincipal.role}`}
                      sizes="56px"
                      className="block w-full h-full"
                      imgClassName="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-headline-sm text-[18px] leading-7 text-gold-light">{contactPrincipal.name}</span>
                    <span className="font-label-sm text-label-sm uppercase tracking-wider text-primary-fixed-dim">{contactPrincipal.role}</span>
                  </div>
                </figcaption>
                <blockquote className="font-body-md text-body-md text-surface-container-high italic font-light">
                  “{contactPrincipal.quote}”
                </blockquote>
              </figure>
            </div>

            <div className="lg:col-span-7">
              <ScopingForm />
            </div>
          </div>
        </div>
      </section>

      {/* MATERIALS */}
      <section className="w-full py-space-xl bg-surface-container-low overflow-hidden">
        <div className={container}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-gutter gap-y-space-lg items-center">
            <div className="reveal md:col-span-5 flex flex-col gap-space-sm">
              <span className={`${eyebrow} font-bold`}>Tactile Verification</span>
              <h2 className={h2Section}>Touch the Stone. Examine the Grain.</h2>
              <p className="font-body-lg text-body-lg text-on-surface-variant font-light">
                Renderings depict intentions, but materials evoke presence. Our studio sample archives harbor rare Italian
                Botticino, fluted Burmese teak samples, custom brass trims, and acoustically damped fabric panels.
              </p>
              <dl className="flex items-center gap-space-md pt-space-xs">
                {materialStats.map((s, i) => (
                  <div key={s.label} className="flex items-center gap-space-md">
                    {i > 0 && <span className="w-px h-10 bg-secondary/30" aria-hidden="true" />}
                    <div className="flex flex-col-reverse">
                      <dt className="font-label-sm text-label-sm uppercase text-on-surface-variant">{s.label}</dt>
                      <dd className="font-headline-sm text-[28px] leading-9 text-primary">{s.value}</dd>
                    </div>
                  </div>
                ))}
              </dl>
            </div>
            <div className="md:col-span-7 grid grid-cols-2 items-start gap-space-sm sm:gap-space-md">
              <div className="reveal rounded overflow-hidden shadow-lg group bg-surface-container">
                <Picture
                  name="contact-marble-walnut"
                  alt="Polished Calacatta marble meeting dark walnut joinery with a brushed brass inlay"
                  sizes="(min-width: 768px) 28vw, 50vw"
                  className="block w-full"
                  imgClassName="w-full h-56 sm:h-80 object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="reveal rounded overflow-hidden shadow-lg mt-8 group bg-surface-container">
                <Picture
                  name="contact-walk-in-wardrobe"
                  alt="Walk-in wardrobe with warm LED cove lighting, frosted glass display cases and green accents"
                  sizes="(min-width: 768px) 28vw, 50vw"
                  className="block w-full"
                  imgClassName="w-full h-56 sm:h-80 object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="w-full py-space-xl bg-surface">
        <div className={container}>
          <div className="reveal flex flex-col items-center text-center mb-space-xl">
            <span className={`${eyebrow} font-bold`}>Transparency &amp; Protocol</span>
            <h2 className={`${h2Section} mt-1`}>Frequently Addressed Inquiries</h2>
            <p className="font-body-md text-body-md text-on-surface-variant max-w-xl mt-space-xs">
              Clear answers concerning our turnkey delivery model, territorial reach, timeline milestones, and enduring
              guarantees.
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Faq items={contactFaqs} />
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="w-full bg-botanical-dark text-on-primary py-space-xl relative overflow-hidden">
        <div className={`${container} relative z-10 flex flex-col md:flex-row items-center justify-between gap-space-lg`}>
          <div className="flex flex-col gap-space-xs text-center md:text-left">
            <span className="font-label-sm text-label-sm uppercase tracking-widest text-gold-light">Direct Partner Line</span>
            <h2 className="font-headline-md text-headline-md text-on-primary">Prefer a Direct Architectural Dialogue?</h2>
            <p className="font-body-md text-body-md text-surface-container-high max-w-xl">
              Call our principal office directly or schedule a private physical walkthrough at the Praise Complex atelier in
              Nagercoil.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-space-md">
            <a
              href={`tel:${SITE.phoneE164}`}
              className="inline-flex items-center gap-space-xs bg-gold-light text-primary hover:bg-surface-ivory px-space-lg py-space-md rounded font-label-lg text-label-lg uppercase tracking-wider transition-all duration-300 shadow-xl"
            >
              <Icon name="phone_in_talk" size={20} />
              <span>{SITE.phoneDisplay}</span>
            </a>
            <a
              href={SITE.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-space-xs text-gold-light hover:text-on-primary font-label-lg text-label-lg uppercase tracking-wider transition-colors"
            >
              <span>Message on WhatsApp</span>
              <Icon name="arrow_forward" size={18} />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
